/* globals $ */
import npmurl from 'url';
import gravatar from 'gravatar';

import React from 'react';

/**
 * Helper for all of GoSearch
 *
 * Exporting individual functions is preferred to allow for tree shaking of functions
 */

/**
 * Function takes in a function as a parameter.
 * This will usually be the click handler
 * This function can ingest the click handler, and create a
 * keydown handler when the enter key is pressed.
 *
 * Example:
 * handleSomeClick = (e) => {console.log(e)};
 *
 * <button
 *    onClick={handleSomeClick}
 *    onKeyDown={handleEnterKeyDown(handleSomeClick)}
 * >...
 *
 * @param {*} handler
 * @returns
 */
export const handleEnterKeyDown = handler => e => {
  if (e.code === 'Enter') {
    handler(e);
  }
};

/**
 * Retrieve the cookie name
 *
 * @param {string} cookieName
 * @returns {string}
 */
export const getCookie = cookieName => {
  const b = document.cookie.match(`(^|;)\\s*${cookieName}\\s*=\\s*([^;]+)`);
  return b ? b.pop() : '';
};

/**
 * Escape user input prior to using it for regex
 *
 * @param {string} user input string
 * @returns {string} escaped string
 */
export const escapeRegExp = string => {
  // $& means the whole matched string
  string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Escape HTML from user submitted input. Used native browser filtering
 *
 * @param {string} user input string
 * @returns {string} escaped string
 */
export const escapeHTML = (string = '') => {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(string));
  return div.innerHTML;
};

/**
 * Scroll to the top of the page smooth
 */
export const scrollToTop = () => {
  $('html, body').animate(
    {
      scrollTop: 0,
    },
    'slow'
  );
};

/**
 * Used for trimming tabs off of a JavaScript template
 */
export const trim = input => input.replace(/\t/, '').replace(/\s{2,}/, ' ');

/**
 * Trims a given string of leading or trailing spaces, as well as 2+ spaces in a row.
 * e.g. "   hi    there " -> "hi there"
 * @param {string} input to trim
 * @returns trimmed input
 */
export const trimSpaces = input => (input ? `${input}`.trim().replace(/\s\s+/g, ' ') : '');

/**
 * Shows an alert box. success, info, warning, danger. Defaults to danger
 * @param title
 * @param message
 * @param type
 */

export const numToString = num => {
  let stringVal = 0;

  if (num >= 1000000) {
    stringVal = (num / 1000000).toFixed(1);
    stringVal = `${stringVal}M`;
  } else if (num >= 1000) {
    stringVal = (num / 1000).toFixed(1);
    stringVal = `${stringVal}K`;
  } else {
    stringVal = num.toString();
  }
  return stringVal;
};

/**
 * Serialize the invite form data into a string to load into the bulk invite form
 *
 * @param {string} first
 * @param {string} last
 * @param {string} color - Hex representation
 */
export const avatar = (email, first = '', last = '', color = '999') => {
  const [f] = encodeURIComponent(first.replace(/,;<>/).trim()) || ['']; // first character of first name
  const [l] = encodeURIComponent(last.replace(/,;<>/).trim()) || ['']; // first character of last name
  const c = encodeURIComponent(color.replace('#', '')) || color; // background color
  const name = encodeURIComponent([f, l].join(' ').trim() || email[0]); // first+last or email
  const s = 40; // size
  const fc = 'FFF'; // font color

  // default URL
  const url = `https://ui-avatars.com/api?name=${name}&rounded=true&background=${c}&size=${s}&color=${fc}`;
  const d = `https://ui-avatars.com/api/${name}/${s}/${c}/${fc}/2/0.5/true`;

  // If the email is valid, return gravatar, otherwise return ui-avatar
  // Allows for caching of url to avoid flickering and multiple calls
  return /\S+@\S+\.\S{2,}/g.test(email) ? gravatar.url(email, { d, s }) : url;
};

export const editedClass = seconds => {
  let stringVal = '';

  if (seconds < 60 * 60 * 24 * 7) {
    stringVal = 'text-danger';
  } else if (seconds < 60 * 60 * 24 * 31) {
    stringVal = 'text-warning';
  } else {
    stringVal = '';
  }
  return stringVal;
};

export const createLoadingSpinner = () => {
  const spinnerStyle = {
    width: '5rem',
    height: '5rem',
  };
  return (
    <div className="d-flex justify-content-center mt-2">
      <div className="spinner-grow text-gray" style={spinnerStyle} role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
};

/**
 * Gets a url parameter by name/names. Passing in an individual name/key will return one value.
 * Passing in an array of names/keys will return an array of values in the same order as the keys.
 * Special case for `username` parameter in that `+` symbols will not get converted to spaces
 * like they would normally and instead are preserved.
 *
 * @param {string | string[]} names
 * @param href
 * @returns {string | string[]}
 */
export const getParameterByName = (names, href = window.location.href) => {
  const parse = npmurl.parse(href, true);
  const namesIsArray = Array.isArray(names);

  const parseQuery = key => {
    const query = parse.query[key] || '';
    const parsedQuery = decodeURIComponent(query);

    /**
     * `+` is decoded to a space. Put the `+`s back in if thats what we had.
     * Just for usernames which can take the form of eg: `first.last+wow`.
     * If it is already properly encoded to a `%2B` symbol then it will
     * be decoded to a `+` as usual.
     */
    if (key === 'username' && parsedQuery) {
      return parsedQuery.replace(/ /g, '+');
    }

    return parsedQuery;
  };

  return namesIsArray ? names.map(parseQuery) : parseQuery(names);
};

export const gravatarURL = (email, defaultPic = 'identicon') =>
  gravatar.url(email, { protocol: 'https', default: defaultPic });

/**
 * Parses a service identifier string into its service name and ciid
 *
 * @param {string} appName - The service identifier string to parse
 * @returns {Object} An object containing the `serviceName` (string) and `ciid` (string or null)
 */
export const parseServiceIdentifier = (appName) => {
  /**
   * Split by the hyphen and check if the last part is numeric where the number represents the ciid
   * @example `jira-1211` => serviceName = `jira`, ciid = 1211
   * @example `google-drive-personal` => serviceName = `google-drive-personal`, ciid = null
   */
  const serviceNameParts = appName.split('-');
  const isLastPartNumeric = /^\d+$/.test(serviceNameParts[serviceNameParts.length - 1]);

  const name = isLastPartNumeric ? serviceNameParts.slice(0, -1).join('-') : appName;
  const ciid = isLastPartNumeric ? serviceNameParts[serviceNameParts.length - 1] : null;

  return {
    name,
    ciid
  };
};
